import React from 'react';
import { FaClock, FaUsers, FaArrowRight, FaCertificate, FaBookOpen, FaLaptop, FaChartLine, FaGlobe } from 'react-icons/fa';
import { MdArtTrack, MdScience, MdBusinessCenter, MdComputer } from 'react-icons/md';
import { GiMicroscope, GiBrain, GiBookshelf, GiTeacher } from 'react-icons/gi';

const Courses = () => {
  const courses = [
    {
      name: "Bachelor of Arts (B.A.)",
      short: "B.A.",
      icon: MdArtTrack,
      color: "from-blue-600 to-cyan-500",
      duration: "3 Years",
      seats: 180,
      fee: "₹12,000/year",
      description: "Comprehensive arts education with a wide range of specializations in humanities and social sciences. Affiliated to Veer Bahadur Singh Purvanchal University, Jaunpur.",
      specializations: [
        "हिन्दी (Hindi)", "समाजशास्त्र (Sociology)", "शिक्षा शास्त्र (Education)", 
        "गृह विज्ञान (Home Science)", "अंग्रेजी (English)", "अर्थशास्त्र (Economics)",
        "भूगोल (Geography)", "प्राचीन इतिहास (Ancient History)", 
        "राजनीति शास्त्र (Political Science)", "संस्कृत (Sanskrit)"
      ],
      career: "Teaching, Civil Services, Journalism, Social Work, Research",
      highlights: ["Experienced Faculty", "Library with 10,000+ Books", "Guest Lectures", "Field Trips"]
    },
    {
      name: "Bachelor of Science (B.Sc.)",
      short: "B.Sc.",
      icon: GiMicroscope,
      color: "from-green-500 to-emerald-500",
      duration: "3 Years",
      seats: 120,
      fee: "₹15,000/year",
      description: "Advanced science education with state-of-the-art laboratories. Focus on practical learning and research methodology in pure sciences.",
      specializations: [
        "भौतिक विज्ञान (Physics)", "रसायन विज्ञान (Chemistry)",
        "वनस्पति विज्ञान (Botany)", "प्राणि विज्ञान (Zoology)", "गणित (Mathematics)"
      ],
      career: "Research Scientist, Data Analyst, Teaching, Environmental Science, Laboratory Manager",
      highlights: ["Modern Labs", "Research Projects", "Industry Visits", "Seminar Series"]
    },
    {
      name: "Bachelor of Business Administration (BBA)",
      short: "BBA",
      icon: MdBusinessCenter,
      color: "from-blue-300 to-blue-400",
      duration: "3 Years",
      seats: 60,
      fee: "₹20,000/year",
      description: "Professional management program focusing on business principles, leadership, and entrepreneurial skills.",
      specializations: ["Marketing", "Finance", "Human Resource", "Business Analytics"],
      career: "Business Analyst, Marketing Manager, Entrepreneur, HR Executive",
      highlights: ["Industry Projects", "Internship Opportunities", "Guest Lectures", "Personality Development"]
    },
    {
      name: "Bachelor of Computer Applications (BCA)",
      short: "BCA",
      icon: MdComputer,
      color: "from-indigo-500 to-blue-600",
      duration: "3 Years",
      seats: 90,
      fee: "₹25,000/year",
      description: "Industry-focused IT education with cutting-edge curriculum and practical training in software development.",
      specializations: ["Programming", "Web Development", "Database Management", "Cloud Computing"],
      career: "Software Developer, Web Designer, System Analyst, IT Consultant",
      highlights: ["Modern Computer Lab", "Industry Projects", "Placement Support", "Coding Workshops"]
    },
    {
      name: "Master of Arts (M.A.)",
      short: "M.A.",
      icon: GiBookshelf,
      color: "from-orange-500 to-red-500",
      duration: "2 Years",
      seats: 90,
      fee: "₹18,000/year",
      description: "Advanced postgraduate program for in-depth knowledge and research in selected humanities disciplines.",
      specializations: [
        "हिन्दी (Hindi)", "गृह विज्ञान (Home Science)",
        "समाजशास्त्र (Sociology)", "प्राचीन इतिहास (Ancient History)"
      ],
      career: "Professor, Researcher, Civil Services, Content Strategist, Archivist",
      highlights: ["Research Guidance", "Thesis Writing", "Seminars", "Publication Opportunities"]
    },
    // ----- New courses added below -----
    {
      name: "Master of Science (M.Sc.)",
      short: "M.Sc.",
      icon: GiBrain,
      color: "from-green-600 to-teal-500",
      duration: "2 Years",
      seats: 60,
      fee: "₹20,000/year",
      description: "Advanced postgraduate program in pure and applied sciences with emphasis on research and specialization. Ideal for students aiming for academic or R&D careers.",
      specializations: ["Physics", "Chemistry", "Mathematics", "Botany", "Zoology"],
      career: "Research Scientist, Academician, Lab Manager, Data Scientist, Environmental Consultant",
      highlights: ["Advanced Labs", "Research Projects", "Thesis Guidance", "Industry Collaborations", "Seminars"]
    },
    {
      name: "Diploma in Elementary Education (D.El.Ed)",
      short: "D.El.Ed",
      icon: GiTeacher,
      color: "from-purple-500 to-pink-500",
      duration: "2 Years",
      seats: 100,
      fee: "₹8,000/year",
      description: "Professional teacher training program approved by NCTE, designed to prepare skilled elementary school teachers with modern pedagogical techniques.",
      specializations: ["Teaching Methodology", "Child Psychology", "Educational Technology", "School Management"],
      career: "Primary School Teacher, Education Coordinator, Curriculum Developer, Educational Consultant",
      highlights: ["Practice Teaching", "Workshops", "Child Psychology Training", "NCTE Approved", "Placement Assistance"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50/30">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-800 via-indigo-700 to-purple-800 py-20 text-center text-white">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Courses</h1>
        <div className="w-20 h-1 bg-yellow-400 mx-auto rounded-full mb-4"></div>
        <p className="text-lg max-w-2xl mx-auto">
          Affiliated to <strong>Veer Bahadur Singh Purvanchal University, Jaunpur</strong><br />
          Code: <strong>686</strong>
        </p>
      </div>

      <div className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          {courses.map((course, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden">
              <div className={`h-1.5 bg-gradient-to-r ${course.color}`}></div>
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className={`w-14 h-14 bg-gradient-to-br ${course.color} rounded-xl flex items-center justify-center mb-4`}>
                    <course.icon className="text-white text-2xl" />
                  </div>
                  <span className="text-xs font-semibold text-white bg-blue-600 px-3 py-1 rounded-full">
                    {course.duration}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{course.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{course.description}</p>

                {/* Specializations */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Specializations:</h4>
                  <div className="flex flex-wrap gap-2">
                    {course.specializations.map((spec, i) => (
                      <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Quick Info */}
                <div className="grid grid-cols-3 gap-3 mb-4 pb-3 border-b border-gray-100">
                  <div className="text-center">
                    <FaClock className="text-blue-600 mx-auto text-sm mb-1" />
                    <p className="text-xs text-gray-500">Duration</p>
                    <p className="text-sm font-semibold">{course.duration}</p>
                  </div>
                  <div className="text-center">
                    <FaUsers className="text-blue-600 mx-auto text-sm mb-1" />
                    <p className="text-xs text-gray-500">Seats</p>
                    <p className="text-sm font-semibold">{course.seats}</p>
                  </div>
                  <div className="text-center">
                    <FaCertificate className="text-blue-600 mx-auto text-sm mb-1" />
                    <p className="text-xs text-gray-500">Fee/Year</p>
                    <p className="text-sm font-semibold">{course.fee}</p>
                  </div>
                </div>

                {/* Career Paths */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-700">Career Opportunities:</p>
                  <p className="text-xs text-gray-500">{course.career}</p>
                </div>

                {/* Highlights */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {course.highlights.map((hl, i) => (
                    <span key={i} className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full">{hl}</span>
                  ))}
                </div>

                <button className="w-full py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2 hover:shadow-lg transition">
                  Apply Now <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info – improved phone number visibility */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 text-center">
          <p className="text-gray-800 text-sm font-medium">
            📞 For admission inquiries, contact: <strong className="text-blue-700">7275370808, 9628083886</strong>
          </p>
          <p className="text-gray-600 text-xs mt-2">
            📚 Affiliated to Veer Bahadur Singh Purvanchal University, Jaunpur | College Code: 686
          </p>
        </div>
      </div>
    </div>
  );
};

export default Courses;